import React, { useEffect, useState } from 'react';
import api from '../api';
import Card from '../components/Card';
import CandidateCard from '../components/CandidateCard';
import toast from 'react-hot-toast';

const options = {
  roles: {
    label: 'Analyse des Rôles',
    description:
      "Suggestions de rôles pertinents générées par l'IA pour anticiper les besoins en recrutement.",
  },
  skills: {
    label: 'Compétences en tension',
    description:
      "Identifie les compétences où la demande dépasse l'offre afin de cibler les priorités de recrutement.",
  },
  candidates: {
    label: 'Candidats disponibles par mois',
    description:
      'Visualise la disponibilité des candidats au fil des mois pour anticiper les périodes de pénurie ou de surplus.',
  },
  coverage: {
    label: 'Taux de couverture futur',
    description:
      "Projette la capacité future à couvrir les besoins en comparant les offres disponibles aux demandes prévues.",
  },
};

const AnalyticsPage = () => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Données pour l'analyse des rôles
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Données pour les prévisions
  const [forecast, setForecast] = useState(null);

  const fetchRoles = async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/analytics/potential-roles');
      const rolesData = Array.isArray(res.data) ? res.data : [];
      setRoles(rolesData);
      if (rolesData.length > 0) {
        setSelectedRole(rolesData[0].role);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des rôles IA', err);
      toast.error("Impossible de charger les suggestions de l'IA.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchForecast = async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/analytics/forecast');
      const data = res.data;
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid forecast format');
      }
      data.skills_in_tension = Array.isArray(data.skills_in_tension)
        ? data.skills_in_tension
        : [];
      data.candidates_per_month = data.candidates_per_month || {};
      data.future_coverage_rate = data.future_coverage_rate ?? 0;
      setForecast(data);
    } catch (err) {
      console.error('Erreur lors du chargement des prévisions IA', err);
      toast.error("Impossible de charger les prévisions de l'IA.");
    } finally {
      setIsLoading(false);
    }
  };

  // Charge les données en fonction de l'option choisie
  useEffect(() => {
    if (selectedOption === 'roles' && roles.length === 0) {
      fetchRoles();
    }
    if (
      ['skills', 'candidates', 'coverage'].includes(selectedOption) &&
      !forecast
    ) {
      fetchForecast();
    }
  }, [selectedOption]);

  // Filtrage et sélection du rôle
  const filteredRoles = Array.isArray(roles)
    ? roles.filter((r) =>
        r.role.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  useEffect(() => {
    if (
      filteredRoles.length > 0 &&
      !filteredRoles.some((r) => r.role === selectedRole)
    ) {
      setSelectedRole(filteredRoles[0].role);
    } else if (filteredRoles.length === 0) {
      setSelectedRole(null);
    }
  }, [filteredRoles, selectedRole]);

  const skillsInTension = Array.isArray(forecast?.skills_in_tension)
    ? forecast.skills_in_tension
    : [];
  const candidatesPerMonth =
    forecast?.candidates_per_month &&
    typeof forecast.candidates_per_month === 'object'
      ? forecast.candidates_per_month
      : {};

  return (
    <div className="animate-fade-in space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-pro-dark-text-primary">
          Analytics
        </h1>
        <p className="text-lg text-pro-dark-text-secondary mt-2">
          Choisissez une analyse à effectuer et l'IA vous fournira ses
          résultats.
        </p>
      </div>

      {/* Sélecteur d'options */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(options).map(([key, { label, description }]) => (
          <div key={key} className="relative group">
            <button
              onClick={() => setSelectedOption(key)}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                selectedOption === key
                  ? 'bg-pro-dark-accent text-white border-pro-dark-accent'
                  : 'bg-pro-dark-surface text-pro-dark-text-secondary border-pro-dark-border hover:border-pro-dark-accent'
              }`}
            >
              {label}
            </button>
            <div className="pointer-events-none absolute z-10 left-1/2 -translate-x-1/2 top-full mt-2 w-64 p-4 bg-pro-dark-surface border border-pro-dark-border rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
              {description}
            </div>
          </div>
        ))}
      </div>

      {/* Zone d'affichage */}
      {isLoading && (
        <div className="text-center text-pro-dark-text-secondary">
          Analyse en cours...
        </div>
      )}

      {!isLoading && selectedOption === null && (
        <Card>
          <p className="text-center text-pro-dark-text-secondary">
            Sélectionnez une option ci-dessus pour commencer l'analyse.
          </p>
        </Card>
      )}

      {!isLoading && selectedOption === 'roles' && (
        <>
          {roles.length === 0 ? (
            <Card>
              <p className="text-center text-pro-dark-text-secondary">
                Aucune suggestion de rôle n'a pu être générée.
              </p>
            </Card>
          ) : (
            <>
              <div className="mb-4">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un rôle..."
                  className="w-full max-w-sm p-2 bg-pro-dark-surface border border-pro-dark-border rounded-md focus:ring-pro-dark-accent focus:border-pro-dark-accent"
                />
              </div>
              {filteredRoles.length === 0 ? (
                <Card>
                  <p className="text-center text-pro-dark-text-secondary">
                    Aucun rôle ne correspond à votre recherche.
                  </p>
                </Card>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {filteredRoles.map((r) => (
                      <button
                        key={r.role}
                        onClick={() => setSelectedRole(r.role)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                          selectedRole === r.role
                            ? 'bg-pro-dark-accent text-white border-pro-dark-accent'
                            : 'bg-pro-dark-surface text-pro-dark-text-secondary border-pro-dark-border hover:border-pro-dark-accent'
                        }`}
                      >
                        {r.role} ({Array.isArray(r.matches) ? r.matches.length : 0})
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredRoles
                      .find((r) => r.role === selectedRole)
                      ?.matches?.map((m) => (
                        <CandidateCard
                          key={m.candidat.id}
                          candidat={m.candidat}
                          matchPercentage={m.matchPercentage}
                          summary={m.summary}
                        />
                      ))}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {!isLoading && selectedOption === 'skills' && forecast && (
        <Card>
          <div>
            <h3 className="font-semibold mb-2">Compétences en tension</h3>
            {skillsInTension.length === 0 ? (
              <p className="text-pro-dark-text-secondary">
                Aucune compétence identifiée.
              </p>
            ) : (
              <>
                <p className="text-xs text-pro-dark-text-secondary mb-2">
                  Liste nettoyée par l'IA
                </p>
                <div className="flex flex-wrap gap-2">
                  {skillsInTension.map((s) => (
                    <span
                      key={s.skill}
                      className="px-3 py-1 text-sm rounded-full border bg-pro-dark-surface text-pro-dark-text-secondary border-pro-dark-border"
                    >
                      {s.skill} ({s.tension})
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {!isLoading && selectedOption === 'candidates' && forecast && (
        <Card>
          <div>
            <h3 className="font-semibold mb-2">Candidats disponibles par mois</h3>
            {Object.keys(candidatesPerMonth).length === 0 ? (
              <p className="text-pro-dark-text-secondary">
                Aucune donnée de disponibilité.
              </p>
            ) : (
              <table className="w-full text-left">
                <thead>
                  <tr>
                    <th className="py-1">Mois</th>
                    <th className="py-1">Candidats</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(candidatesPerMonth).map(
                    ([month, count]) => (
                      <tr key={month}>
                        <td className="py-1">{month}</td>
                        <td className="py-1">{count}</td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      )}

      {!isLoading && selectedOption === 'coverage' && forecast && (
        <Card>
          <div>
            <h3 className="font-semibold mb-2">Taux de couverture futur</h3>
            <p>{forecast?.future_coverage_rate ?? 0}%</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsPage;

